import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

/**
 * Data access for the Data Import page.
 *
 * `POST /api/admin/imports` is the only upload in the application. It goes through the same
 * `api` helper as everything else: that helper already passes a `FormData` body through
 * untouched — without forcing a JSON content type, which would strip the multipart boundary —
 * while still attaching the antiforgery header every mutating call needs.
 *
 * Nothing here polls a running job. Progress arrives over the SignalR hub
 * (`useImportStream`); these queries cover the surrounding state — who is importing right now,
 * and what ran before.
 */

const BASE = '/api/admin/imports';

/** Mirrors `ImportFileRoles`; the server is still the authority, via `useImportCampaigns`. */
export type ImportFileRole =
  | 'garages'
  | 'calls'
  | 'appointments'
  | 'regionalStats'
  | 'subRegionalStats'
  | 'thresholds';

export type ImportMode = 'DryRun' | 'New' | 'Update';

export type ImportJobStatus = 'Queued' | 'Running' | 'Succeeded' | 'Failed' | 'Cancelled';

export interface ImportCampaign {
  /** Posted on submission, stored on the job, and shown as-is: "Takata", "After Sales". */
  campaign: string;
  requiredFileRoles: ImportFileRole[];
  optionalFileRoles: ImportFileRole[];
}

export interface ImportJob {
  id: string;
  campaign: string;
  mode: ImportMode;
  status: ImportJobStatus;
  requestedBy: string;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  cancelRequested: boolean;
  fileNames: Partial<Record<ImportFileRole, string>>;
  /** The pipeline's final counts; shape depends on the campaign. */
  summary: Record<string, unknown> | null;
  error: string | null;
  isActive: boolean;
}

/** `kind` decides how the page treats an event, `level` how it colours it. */
export type ImportEventKind = 'log' | 'step' | 'summary' | 'orphans';
export type ImportEventLevel = 'info' | 'warn' | 'error';

export interface ImportEvent {
  seq: number;
  jobId: string;
  ts: string;
  level: ImportEventLevel;
  kind: ImportEventKind;
  message: string | null;
  payload: unknown;
}

/** Payload of a `step` event. */
export interface ImportStepPayload {
  name: string;
  current: number;
  total: number;
}

export const importKeys = {
  all: ['admin', 'imports'] as const,
  campaigns: ['admin', 'imports', 'campaigns'] as const,
  list: ['admin', 'imports', 'list'] as const,
  active: ['admin', 'imports', 'active'] as const,
  events: (jobId: string) => ['admin', 'imports', 'events', jobId] as const,
};

/** The campaigns and their file requirements, so the form is not a second source of truth. */
export function useImportCampaigns() {
  return useQuery<ImportCampaign[]>({
    queryKey: importKeys.campaigns,
    queryFn: () => api.get<ImportCampaign[]>(`${BASE}/campaigns`),
    // Fixed by the deployed build; no reason to refetch it during a session.
    staleTime: Infinity,
  });
}

export function useImportJobs(limit = 25) {
  return useQuery<ImportJob[]>({
    queryKey: [...importKeys.list, limit],
    queryFn: () => api.get<ImportJob[]>(BASE, { query: { limit } }),
  });
}

/**
 * The run currently holding the single-run slot, on any instance, or null.
 *
 * Polled rather than pushed: an import started by another administrator has to disable this
 * one's submit button, and until we know its id there is no hub group to be in. The interval
 * is short while something is running so the banner clears promptly when it ends.
 */
export function useActiveImport() {
  return useQuery<ImportJob | null>({
    queryKey: importKeys.active,
    // 204 comes back as undefined from the api helper; normalise it to null.
    queryFn: async () => (await api.get<ImportJob | undefined>(`${BASE}/active`)) ?? null,
    refetchInterval: (query) => (query.state.data ? 3000 : 15000),
  });
}

export function useImportJob(jobId: string | null) {
  return useQuery<ImportJob>({
    queryKey: [...importKeys.all, 'job', jobId],
    queryFn: () => api.get<ImportJob>(`${BASE}/${jobId}`),
    enabled: jobId !== null,
  });
}

/** A job's log from the REST side: the initial fill, and the catch-up after a dropped socket. */
export function useImportEvents(jobId: string | null, enabled: boolean) {
  return useQuery<ImportEvent[]>({
    queryKey: importKeys.events(jobId ?? ''),
    queryFn: () => api.get<ImportEvent[]>(`${BASE}/${jobId}/events`),
    enabled: jobId !== null && enabled,
  });
}

export interface SubmitImportInput {
  campaign: string;
  mode: ImportMode;
  /** One entry per role the operator filled in. */
  files: Partial<Record<ImportFileRole, File>>;
}

export function useSubmitImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaign, mode, files }: SubmitImportInput) => {
      const body = new FormData();
      body.append('campaign', campaign);
      body.append('mode', mode);
      // The field name is the role: that is how the server tells the CSVs apart, so a file
      // picked into the wrong input is rejected rather than silently imported as another.
      for (const [role, file] of Object.entries(files)) {
        if (file) body.append(role, file, file.name);
      }
      return api.post<ImportJob>(BASE, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: importKeys.list });
      queryClient.invalidateQueries({ queryKey: importKeys.active });
    },
  });
}

export function useCancelImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => api.post<void>(`${BASE}/${jobId}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: importKeys.active });
    },
  });
}

/** Mirrors `ImportOrphanSets`: which input file an `orphans` event's rows came from. */
export type ImportOrphanSet = 'calls' | 'appointments';

/**
 * The set an `orphans` event belongs to. Events logged before sets existed carry none; they
 * were always orphan calls.
 */
export function orphanSetOf(event: ImportEvent): ImportOrphanSet {
  const set = (event.payload as { set?: unknown } | null)?.set;
  return set === 'appointments' ? 'appointments' : 'calls';
}

/** Where one set of a run's orphan rows can be downloaded. */
export function orphansUrl(jobId: string, set: ImportOrphanSet): string {
  return `${BASE}/${jobId}/orphans/${set}.csv`;
}

/**
 * The i18n key for a campaign's label.
 *
 * The campaign is already a readable name, so this only exists to let a culture override it
 * ("After Sales" → "Post-vendita"). Spaces come out of the key because i18next keys are flat
 * strings and a space in one reads as a typo; the stored name is the default, so a campaign
 * nobody has translated still shows correctly.
 */
export function campaignLabelKey(campaign: string): string {
  return `DataImport.CampaignName.${campaign.replace(/\s+/g, '')}`;
}
